import { Component, computed, input, output, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, FormsModule, ReactiveFormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NeoFormElementsModule, NeoFormRichTextFroalaComponent } from '@neogov/pattern-library/form-elements';
import FroalaEditor from 'froala-editor';

@Component({
  selector: 'cm-rich-textarea',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, NeoFormElementsModule],
  templateUrl: './rich-textarea.component.html',
  styleUrl: './rich-textarea.component.less',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: RichTextareaComponent,
      multi: true,
    },
  ],
})
export class RichTextareaComponent implements ControlValueAccessor {
  public readonly label = input<string>('');
  public readonly placeholder = input<string>('');
  public readonly autId = input.required<string>();
  /**
   * Validation message folded into the editor's accessible name when set, so a
   * screen-reader user focused in the Froala region hears the error (WCAG 3.3.1).
   * The Froala wrapper only exposes `ariaLabel`, so we cannot use aria-describedby.
   */
  public readonly invalidMessage = input<string>('');

  /** Accessible name for the editor, appended with the validation error when present. */
  public readonly accessibleName = computed(() => {
    const base = this.label() || this.placeholder();
    const error = this.invalidMessage().trim();
    return error ? `${base}. ${error}` : base;
  });

  private readonly _froalaRef = viewChild(NeoFormRichTextFroalaComponent);

  public readonly focused = output<void>();

  public readonly toolbarButtons = computed(() => this._getToolbarButtons());
  public readonly config = computed(() => this._getConfig());

  public value = '';
  public disabled = false;

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched: () => void = () => {};

  public writeValue(value: string): void {
    this.value = value || '';
  }

  public registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public onValueChange(value: string): void {
    this.value = value;
    this._onChange(this.value);
  }

  public onBlur(): void {
    this._onTouched();
  }

  public insertHtml(html: string): void {
    const ref = this._froalaRef();
    if (ref) {
      ref.insertText(html);
      ref.froalaEditor?.events?.trigger('contentChanged', [], true);
    }
  }

  public focus(): void {
    const ref = this._froalaRef();
    ref?.froalaEditor?.events?.focus();
  }

  private _getToolbarButtons(): string[] {
    return [
      'bold',
      'italic',
      'underline',
      'formatOL',
      'formatUL',
      'align',
      'outdent',
      '|',
      'undo',
      'redo'
    ];
  }

  private _getConfig() {
    return {
      enter: FroalaEditor.ENTER_DIV,
      imageUploadRemoteUrls: false,
      imageInsertButtons: [],
      toolbarButtons: this.toolbarButtons(),
      fontFamilySelection: true,
      fontFamilyDefaultSelection: 'Proxima nova',
      fontFamily: {
        'proxima-nova': 'Proxima nova',
        'Arial,Helvetica,sans-serif': 'Arial',
        'Georgia,serif': 'Georgia',
        'Impact,Charcoal,sans-serif': 'Impact',
        'Tahoma,Geneva,sans-serif': 'Tahoma',
        "'Times New Roman',Times,serif": 'Times New Roman',
        'Verdana,Geneva,sans-serif': 'Verdana',
      },
      paragraphMultipleStyles: false,
      paragraphFormat: {
        N: 'Normal',
        H1: 'Header 1',
        H2: 'Header 2',
        H3: 'Header 3',
        H4: 'Header 4',
        H5: 'Small Text',
      },
      zIndex: 2000,
      fullPage: false,
      height: 250,
      events: {
        focus: () => {
          this.focused.emit();
        },
      },
    }
  }
}
